import { COUNTRY } from '../globals';
import { keyBy } from '../utils/array';

import type { Resource } from 'i18next';
import type { BlogCategory } from '../globals';
import type {
  Breadcrumb,
  ContentfulPageBlogNode,
  ContentfulPageContainer,
  ContentfulPageContainerNode,
  PathArray,
} from './models';

export const checkForPathUniqueness = (pages: ContentfulPageContainer[]) => {
  let duplicateFound: boolean = false;
  const hash: Record<string, ContentfulPageContainer[]> = {}

  for (const page of pages) {
    const { path } = page;
    if (hash[path]) hash[path].push(page);
    else hash[path] = [page];
  }

  Object.entries(hash).forEach(([path, pageList]) => {
    if (pageList.length > 1) {
      duplicateFound = true;
      console.warn(`Path '${path}' is used by multiple pages:`);
      pageList.forEach(({ id, name }) => console.warn(`-> '${name}' (id: ${id})`));
    }
  });

  if (duplicateFound) {
    console.error('Duplicated path(s) found. See above.');
    process.exit(1);
  }
};

export const checkForOrphans = (nodes: ContentfulPageContainerNode[]) => {
  let orphanFound: boolean = false;
  const hash = keyBy(nodes, 'id');
  const logWarning = (message: string) => {
    orphanFound = true;
    console.warn(message);
  };

  for (const { id, name, slug, parentPage } of nodes) {
    if (slug === '/') continue;
    if (!parentPage) logWarning(`Page '${name}' has no parent.`);
    if (parentPage && !hash[parentPage.id]) logWarning(`Page '${name}' is referencing a parent that doesn't exist.`);
    if (id === parentPage?.id) logWarning(`Page '${name}' is referencing itself as its own parent.`);
  }

  if (orphanFound) {
    console.error('Orphan page(s) found. See above.');
    process.exit(1);
  }
};

export const createPathArray = (
  node: ContentfulPageContainerNode,
  hash: Record<string, ContentfulPageContainerNode>,
): PathArray => {
  const pathArray: PathArray = [];
  const visitedNodes: string[] = [];
  let currentNode: ContentfulPageContainerNode | null = node;

  while (currentNode) {
    const { id, slug, metaFields } = currentNode;
    if (visitedNodes.includes(id)) {
      console.error(
        `Circular reference detected: ${visitedNodes.slice(visitedNodes.indexOf(id)).map((id) => `'${hash[id].name}'`).join(' -> ')} -> '${hash[id].name}'`
      );
      process.exit(1);
    }
    visitedNodes.push(id);
    pathArray.unshift({
      label: metaFields.metaTitle,
      slug,
    });
    currentNode = currentNode.parentPage?.id ? hash[currentNode.parentPage.id] : null;
  }
  return pathArray;
};

export const createPathFromPathArray = (pathArray: PathArray): string =>
  pathArray.reduce((acc, { slug }) => acc += slug === '/' ? '' : slug.replace(/^\/*/, '/').replace(/\/*$/, ''), '') + '/';

export const createBreadcrumbsFromPathArray = (pathArray: PathArray): Breadcrumb[] =>
  pathArray.map(({ label }, index) => ({
    label,
    path: createPathFromPathArray(pathArray.slice(0, ++index)),
  }));

export const createPageHierarchy = (nodes: ContentfulPageContainerNode[]): ContentfulPageContainer[] => {
  const hash = keyBy(nodes, 'id');
  return nodes.map((node) => {
    const pathArray = createPathArray(node, hash);
    return {
      id: node.id,
      name: node.name,
      slug: node.slug,
      variants: node.variants,
      path: createPathFromPathArray(pathArray),
      breadcrumbs: createBreadcrumbsFromPathArray(pathArray),
    };
  });
};

/**
 * Convert Contentful blog category display name to slug.
 * Example input: `NL - Improve your business (Beter ondernemen) [/beter-ondernemen]`
 * Example output: `beter-ondernemen`
 */
export const convertBlogCategoryToSlug = (value: string): string => {
  const openingBracketWithSlash = value.indexOf('[/');
  const closingBracket = value.indexOf(']');

  if (openingBracketWithSlash !== -1 && closingBracket !== -1) {
    const slug = value.slice(openingBracketWithSlash + 1, closingBracket);
    return slug === '/' ? '' : slug;
  } else {
    console.error(`Blog category cannot be extracted from '${value}'.`);
    process.exit(1);
  }
};

/**
 * Extract Contentful blog category display name.
 * Example input: `NL - Improve your business (Beter ondernemen) [/beter-ondernemen]`
 * Example output: `Beter Ondernemen`
 */
export const extractBlogCategory = (value: string): string => {
  const openingParenthesis = value.indexOf('(');
  const closingParenthesis = value.indexOf(')');

  if (openingParenthesis !== -1 && closingParenthesis !== -1) {
    return value.slice(openingParenthesis + 1, closingParenthesis);
  } else {
    console.error(`Blog category cannot be extracted from '${value}'.`);
    process.exit(1);
  }
};

/**
 * Create list of blog categories from articles and sort them alphabetically with 'All articles' in front
 */
export const getBlogCategoriesFromArticles = (
  nodes: ContentfulPageBlogNode[],
  i18nResources: Resource,
): BlogCategory[] => {
  const categorySet = new Set<string>();

  nodes.forEach(({ category }) => {
    categorySet.add(category);
  });

  const all = {
    contentfulName: '',
    name: (i18nResources[COUNTRY].common as any).blog_category_all,
    path: '/blog/',
  };

  const categories = Array.from(categorySet.values())
    .map((category) => ({
      contentfulName: category,
      name: extractBlogCategory(category),
      path: `/blog${convertBlogCategoryToSlug(category)}/`,
    }))
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

  return [all, ...categories];
};
